import React, { useCallback, useMemo } from 'react'
import { Dimensions, FlatList, Platform, Pressable, StyleSheet, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useQuery } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { DS } from '@/design-system'

type Registration = {
  userId: string
  userName: string
  registeredAt: number
}

// Stable empty list so the `onlineSet` `useMemo` dep doesn't churn while
// the dedicated presence query is still loading.
const EMPTY_ONLINE: readonly string[] = []

interface Props {
  /** Tournament the drawer is rendering. Drives the presence subscription. */
  tournamentId: Id<'tournaments'>
  registrations: Registration[]
  registrationCount: number
  hostUserId: string
  cohostUserIds: string[]
  /** Authenticated user's auth0 subject. */
  currentUserId: string
  /** Whether the current user is the host (only host can toggle co-hosts). */
  canManageCohosts: boolean
  /** Tap a row to open the cohost management modal. */
  onSelectCohostTarget: (target: { userId: string; userName: string; isCohost: boolean }) => void
  /** Backdrop tap closes the drawer. */
  onClose: () => void
}

/**
 * Drawer content listing all registered players with online indicator and
 * host/co-host badges. Replaces an inline `.map` over a non-virtualized
 * `ScrollView` — uses `FlatList` so 100+ player tournaments don't render
 * every row up-front.
 *
 * Owns its own presence subscription (`getTournamentPresence`) so presence
 * pings don't invalidate the lobby's larger subscription. The subscription
 * is bounded to "drawer mounted" — i.e. participant + drawer-open.
 *
 * Wrapped in `React.memo` so the drawer doesn't re-render purely because
 * the parent re-rendered. The parent must pass stable references for the
 * callback props (`onSelectCohostTarget`, `onClose`) and for the
 * `cohostUserIds` array — use a module-scope stable empty array rather
 * than inline `?? []` in the caller. Without those, the memo shield is
 * defeated and the FlatList tears down its rows.
 */
export const TournamentPlayerDrawer = React.memo(function TournamentPlayerDrawer({
  tournamentId,
  registrations,
  registrationCount,
  hostUserId,
  cohostUserIds,
  currentUserId,
  canManageCohosts,
  onSelectCohostTarget,
  onClose
}: Props) {
  // Dedicated presence subscription. Lives here (and only here) so that
  // presence pings — which fire every ~30s per participant — don't
  // invalidate the lobby screen's larger subscription. The drawer is only
  // rendered when the user is a participant AND the drawer is open, so
  // the subscription is naturally bounded to "actively viewing the
  // player list".
  const presence = useQuery(api.tournaments.queries.getTournamentPresence, { tournamentId })
  const onlineUserIds = presence?.onlineUserIds ?? EMPTY_ONLINE

  // Memoize the lookup Sets so `renderItem` keeps a stable identity.
  // Keying on the joined arrays keeps re-build cost bounded by the
  // registration cap (500). The dedicated presence query above means
  // these only invalidate on actual presence changes, not on every
  // registration / config / state update.
  const onlineSet = useMemo(() => new Set(onlineUserIds), [onlineUserIds.join('|')])
  const cohostSet = useMemo(() => new Set(cohostUserIds), [cohostUserIds.join('|')])

  const renderItem = useCallback(
    ({ item }: { item: Registration }) => {
      const isOnline = onlineSet.has(item.userId)
      const isPlayerHost = item.userId === hostUserId
      const isPlayerCohost = cohostSet.has(item.userId) && !isPlayerHost
      const canToggleThis = canManageCohosts && !isPlayerHost && item.userId !== currentUserId

      const row = (
        <DS.Frame row gap="8" style={styles.playerRow} alignItems="center">
          <DS.Frame row gap="6" alignItems="center" style={{ flex: 1 }}>
            <DS.Text variant="Body Large/Regular" style={{ flexShrink: 1 }}>
              {item.userName}
            </DS.Text>
            {isOnline && <View style={styles.onlineDot} accessibilityLabel="Online" />}
          </DS.Frame>
          {isPlayerHost && (
            <DS.Text variant="Mini/Bold" style={{ color: DS.palette['Brand/Maroon'] }}>
              HOST
            </DS.Text>
          )}
          {isPlayerCohost && (
            <DS.Text variant="Mini/Bold" style={{ color: DS.palette['Brand/Maroon'] }}>
              CO-HOST
            </DS.Text>
          )}
        </DS.Frame>
      )

      if (canToggleThis) {
        return (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={`Manage ${item.userName} as co-host`}
            onPress={() =>
              onSelectCohostTarget({
                userId: item.userId,
                userName: item.userName,
                isCohost: isPlayerCohost
              })
            }
          >
            {row}
          </Pressable>
        )
      }

      return <View>{row}</View>
    },
    [onlineSet, cohostSet, hostUserId, canManageCohosts, currentUserId, onSelectCohostTarget]
  )

  return (
    <Pressable onPress={onClose} style={styles.drawerOverlay}>
      <Pressable
        onPress={(e) => e.stopPropagation()}
        style={[styles.drawerMenuContainer, { paddingTop: Platform.OS === 'android' ? 25 : 0 }]}
      >
        <SafeAreaView style={{ backgroundColor: 'transparent', flex: 1 }}>
          <DS.Text variant="Caption/Bold" color="Global/Black" style={styles.drawerLabel}>
            {registrationCount} PLAYERS
          </DS.Text>
          <FlatList
            data={registrations}
            keyExtractor={(item, index) => item.userId || `${item.userName}-${item.registeredAt}-${index}`}
            renderItem={renderItem}
            initialNumToRender={20}
            maxToRenderPerBatch={20}
            windowSize={10}
            removeClippedSubviews
            contentContainerStyle={styles.playerListContainer}
          />
        </SafeAreaView>
      </Pressable>
    </Pressable>
  )
})

const styles = StyleSheet.create({
  drawerOverlay: {
    flex: 1,
    zIndex: 1,
    width: '100%',
    alignItems: 'flex-end'
  },
  drawerMenuContainer: {
    zIndex: 2,
    height: '100%',
    backgroundColor: DS.palette['Brand/Cream'],
    justifyContent: 'flex-end',
    minWidth: Math.max(Math.round(Dimensions.get('window').width * 0.67), Platform.OS === 'web' ? 400 : 280),
    maxWidth: Platform.OS === 'web' ? 480 : ('auto' as any),
    paddingHorizontal: 24
  },
  drawerLabel: {
    fontSize: 12,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    marginBottom: 8,
    textAlign: 'right'
  },
  playerListContainer: {
    paddingTop: 4,
    paddingBottom: 24,
    backgroundColor: 'transparent'
  },
  playerRow: {
    paddingVertical: 8,
    borderBottomColor: DS.palette['Brand/Light Grey'],
    alignItems: 'center'
  },
  onlineDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: DS.palette['Utility/Success']
  }
})
